import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, RefreshCw, ChevronLeft, ChevronRight, LogOut,
  MessageSquare, Globe, Mail, X, Monitor, Smartphone,
  MapPin, Clock, Link, User, Wifi, Calendar,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SOURCE_LABELS = {
  landing: 'Landing', solutions: 'Solutions', boq: 'BoQ', submittals: 'Submittals',
  platform: 'Platform', industries: 'Industries', sme: 'SME', about: 'About',
  contact: 'Contact', other: 'Other',
};
const SOURCE_COLORS = {
  landing:    { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  solutions:  { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  boq:        { bg: '#faf5ff', color: '#7c3aed', border: '#e9d5ff' },
  submittals: { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa' },
  platform:   { bg: '#f0fdfa', color: '#0891b2', border: '#a5f3fc' },
  industries: { bg: '#fdf4ff', color: '#a21caf', border: '#f5d0fe' },
  default:    { bg: '#f8fafc', color: '#64748b', border: '#e2e8f0' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(d) {
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtTime(d) {
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}
function fmtFull(d) {
  return `${fmtDate(d)} · ${fmtTime(d)}`;
}
function msgTime(d) {
  if (!d) return '';
  return new Date(d).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function countryFlag(code) {
  if (!code || code.length !== 2) return '🌍';
  return code.toUpperCase().split('').map(c =>
    String.fromCodePoint(0x1F1E6 - 65 + c.charCodeAt(0))
  ).join('');
}

function parseUA(ua) {
  if (!ua) return { browser: 'Unknown', os: 'Unknown', device: 'Desktop' };
  let browser = 'Unknown';
  if      (ua.includes('Edg'))                             browser = 'Edge';
  else if (ua.includes('OPR') || ua.includes('Opera'))     browser = 'Opera';
  else if (ua.includes('Chrome') && !ua.includes('Edg'))   browser = 'Chrome';
  else if (ua.includes('Firefox'))                         browser = 'Firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome'))browser = 'Safari';

  let os = 'Unknown';
  if      (ua.includes('Windows'))                 os = 'Windows';
  else if (ua.includes('iPhone'))                  os = 'iPhone';
  else if (ua.includes('iPad'))                    os = 'iPad';
  else if (ua.includes('Android'))                 os = 'Android';
  else if (ua.includes('Mac OS'))                  os = 'macOS';
  else if (ua.includes('Linux'))                   os = 'Linux';

  const isMobile = /iPhone|Android|iPad|Mobile/.test(ua);
  return { browser, os, device: isMobile ? 'Mobile' : 'Desktop' };
}

function Badge({ value, map, colorMap }) {
  const label = map?.[value] || value || '—';
  const style = colorMap?.[value] || colorMap?.default || { bg: '#f1f5f9', color: '#64748b', border: '#e2e8f0' };
  return (
    <span style={{ background: style.bg, color: style.color, border: `1px solid ${style.border}` }}
      className="text-[11px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
      {label}
    </span>
  );
}

// ── Session Detail Panel ──────────────────────────────────────────────────────
function DetailPanel({ session, onClose }) {
  const messagesEndRef = useRef(null);
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session]);

  if (!session) return null;

  const ua      = parseUA(session.metadata?.userAgent);
  const loc     = session.metadata?.location;
  const ip      = session.metadata?.ipAddress;
  const referrer = session.metadata?.referrer;

  const locationStr = [loc?.city, loc?.region, loc?.country ? `${countryFlag(loc.country)} ${loc.country}` : null]
    .filter(Boolean).join(', ') || 'Unknown location';

  return (
    <motion.div
      initial={{ x: '100%', opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: '100%', opacity: 0 }}
      transition={{ type: 'spring', stiffness: 300, damping: 32 }}
      className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col"
    >
      {/* Panel header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-slate-50 flex-shrink-0">
        <div>
          <p className="text-slate-800 font-bold text-sm">Session Details</p>
          <p className="text-slate-400 text-[11px] font-mono mt-0.5">{session.sessionId}</p>
        </div>
        <button onClick={onClose} className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {/* ── Visitor Info ── */}
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Visitor Info</p>
          <div className="grid grid-cols-2 gap-3">
            {/* IP */}
            <InfoCard icon={Wifi} label="IP Address" value={ip || 'Unknown'} mono />
            {/* Location */}
            <InfoCard icon={MapPin} label="Location" value={locationStr} />
            {/* Timezone */}
            <InfoCard icon={Clock} label="Timezone" value={loc?.timezone || 'Unknown'} />
            {/* Country */}
            {loc?.country && (
              <InfoCard icon={Globe} label="Country" value={`${countryFlag(loc.country)} ${loc.country}`} />
            )}
            {/* Device */}
            <InfoCard icon={ua.device === 'Mobile' ? Smartphone : Monitor} label="Device" value={`${ua.browser} · ${ua.os}`} />
            {/* Source page */}
            <InfoCard icon={Globe} label="Page Source" value={SOURCE_LABELS[session.source] || session.source || 'Unknown'} />
            {/* Referrer */}
            {referrer && (
              <div className="col-span-2">
                <InfoCard icon={Link} label="Referrer" value={referrer} truncate />
              </div>
            )}
          </div>
        </div>

        {/* ── Session Info ── */}
        <div className="px-5 py-4 border-b border-slate-100">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">Session Info</p>
          <div className="grid grid-cols-2 gap-3">
            <InfoCard icon={Calendar} label="Started" value={fmtFull(session.createdAt)} />
            {session.updatedAt && (
              <InfoCard icon={Clock} label="Last Activity" value={fmtFull(session.updatedAt)} />
            )}
            <InfoCard icon={MessageSquare} label="Messages" value={`${session.messageCount} total`} />
            <InfoCard icon={Globe} label="Language" value={session.language === 'ar' ? 'Arabic 🇦🇪' : 'English 🇬🇧'} />
            {session.leadCaptured && (
              <div className="col-span-2">
                <InfoCard icon={Mail} label="Lead Email" value={session.leadEmail} highlight />
              </div>
            )}
          </div>
        </div>

        {/* ── Full User Agent ── */}
        {session.metadata?.userAgent && (
          <div className="px-5 py-3 border-b border-slate-100">
            <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">User Agent</p>
            <p className="text-[11px] text-slate-500 font-mono leading-relaxed break-all">
              {session.metadata.userAgent}
            </p>
          </div>
        )}

        {/* ── Conversation Thread ── */}
        <div className="px-5 py-4">
          <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">
            Conversation · {session.messageCount} messages
          </p>
          {(!session.messages || session.messages.length === 0) ? (
            <p className="text-slate-400 text-sm text-center py-4">No messages recorded.</p>
          ) : (
            <div className="space-y-3">
              {session.messages.map((msg, i) => (
                <div key={i} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  {msg.role === 'assistant' && (
                    <div className="w-6 h-6 rounded-lg bg-primary/10 border border-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <MessageSquare size={11} className="text-primary" />
                    </div>
                  )}
                  <div className="max-w-[80%] flex flex-col">
                    <div className={`px-3 py-2 rounded-xl text-xs leading-relaxed ${
                      msg.role === 'user'
                        ? 'bg-primary text-white rounded-tr-sm'
                        : 'bg-slate-100 text-slate-700 rounded-tl-sm'
                    }`}>
                      {msg.content}
                    </div>
                    {msg.timestamp && (
                      <span className={`text-[10px] text-slate-400 mt-0.5 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                        {msgTime(msg.timestamp)}
                      </span>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="w-6 h-6 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User size={11} className="text-slate-500" />
                    </div>
                  )}
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}

function InfoCard({ icon: Icon, label, value, mono, truncate, highlight }) {
  return (
    <div className={`rounded-xl px-3 py-2.5 ${highlight ? 'bg-emerald-50 border border-emerald-200' : 'bg-slate-50 border border-slate-100'}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={11} className={highlight ? 'text-emerald-500' : 'text-slate-400'} />
        <span className={`text-[10px] font-semibold uppercase tracking-wide ${highlight ? 'text-emerald-600' : 'text-slate-400'}`}>{label}</span>
      </div>
      <p className={`text-xs font-medium leading-snug ${highlight ? 'text-emerald-700' : 'text-slate-700'} ${mono ? 'font-mono' : ''} ${truncate ? 'truncate' : ''}`}>
        {value || '—'}
      </p>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function AdminChats() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('adminToken');

  const [sessions,      setSessions]      = useState([]);
  const [total,         setTotal]         = useState(0);
  const [page,          setPage]          = useState(1);
  const [totalPages,    setTotalPages]    = useState(1);
  const [loading,       setLoading]       = useState(false);
  const [search,        setSearch]        = useState('');
  const [sourceFilter,  setSourceFilter]  = useState('');
  const [langFilter,    setLangFilter]    = useState('');
  const [selected,      setSelected]      = useState(null);

  const LIMIT = 20;

  const fetchSessions = useCallback(async (pg = 1) => {
    if (!token) { navigate('/admin/login'); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: LIMIT });
      if (search)       params.set('search',   search);
      if (sourceFilter) params.set('source',   sourceFilter);
      if (langFilter)   params.set('language', langFilter);

      const res = await fetch(`${API_BASE}/api/chat/admin/sessions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) { navigate('/admin/login'); return; }
      const data = await res.json();
      setSessions(data.sessions || []);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setPage(pg);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token, navigate, search, sourceFilter, langFilter]);

  useEffect(() => { fetchSessions(1); }, [fetchSessions]);

  const logout = () => { sessionStorage.removeItem('adminToken'); navigate('/admin/login'); };

  const pages = [];
  const pStart = Math.max(1, page - 3);
  const pEnd   = Math.min(totalPages, pStart + 6);
  for (let i = pStart; i <= pEnd; i++) pages.push(i);

  const hasFilters = search || sourceFilter || langFilter;

  // Stats
  const leadCount = sessions.filter(s => s.leadCaptured).length;
  const arCount   = sessions.filter(s => s.language === 'ar').length;

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Top bar */}
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-[#3A66FF] flex items-center justify-center">
            <MessageSquare size={15} className="text-white" />
          </div>
          <div>
            <h1 className="text-slate-800 font-bold text-sm leading-tight">AI Chat Sessions</h1>
            <p className="text-slate-400 text-xs">{total.toLocaleString()} conversations recorded</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate('/admin/leads')} className="text-xs text-slate-500 hover:text-slate-800 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors font-medium">
            Leads
          </button>
          <button onClick={() => navigate('/admin/clients')} className="text-xs text-slate-500 hover:text-slate-800 px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors font-medium">
            Clients
          </button>
          <button onClick={() => fetchSessions(page)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors text-xs font-medium">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          <button onClick={logout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200 transition-colors text-xs font-medium">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Sessions',  value: total,      icon: MessageSquare, color: 'text-primary',     bg: 'bg-blue-50',   border: 'border-blue-100' },
            { label: 'Leads Captured',  value: leadCount,  icon: Mail,          color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
            { label: 'Arabic Sessions', value: arCount,    icon: Globe,         color: 'text-amber-600',   bg: 'bg-amber-50',  border: 'border-amber-100' },
            { label: 'This Page',       value: sessions.length, icon: Calendar, color: 'text-violet-600',  bg: 'bg-violet-50', border: 'border-violet-100' },
          ].map(({ label, value, icon: Icon, color, bg, border }) => (
            <div key={label} className={`${bg} border ${border} rounded-xl px-4 py-3 flex items-center gap-3`}>
              <div className={`w-8 h-8 rounded-lg bg-white border ${border} flex items-center justify-center flex-shrink-0`}>
                <Icon size={15} className={color} />
              </div>
              <div>
                <p className="text-slate-800 font-bold text-lg leading-tight">{value}</p>
                <p className="text-slate-500 text-[11px]">{label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchSessions(1)}
              placeholder="Search session ID or lead email…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20"
            />
          </div>
          <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none text-slate-600 bg-white">
            <option value="">All pages</option>
            {Object.entries(SOURCE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select value={langFilter} onChange={e => setLangFilter(e.target.value)}
            className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none text-slate-600 bg-white">
            <option value="">All languages</option>
            <option value="en">English</option>
            <option value="ar">Arabic</option>
          </select>
          {hasFilters && (
            <button onClick={() => { setSearch(''); setSourceFilter(''); setLangFilter(''); }}
              className="flex items-center gap-1 px-3 py-2 text-xs rounded-lg border border-slate-200 text-slate-500 hover:text-slate-700 transition-colors">
              <X size={12} /> Clear
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  {['Session', 'Visitor', 'Page', 'Language', 'Msgs', 'Last Message', 'Lead', 'Started'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-400 text-sm">
                      <RefreshCw size={18} className="animate-spin mx-auto mb-2" />
                      Loading sessions…
                    </td>
                  </tr>
                ) : sessions.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-400 text-sm">No sessions found.</td>
                  </tr>
                ) : sessions.map(s => {
                  const loc = s.metadata?.location;
                  const ua  = parseUA(s.metadata?.userAgent);
                  const isSelected = selected?.sessionId === s.sessionId;

                  return (
                    <tr
                      key={s._id || s.sessionId}
                      onClick={() => setSelected(isSelected ? null : s)}
                      className={`border-b border-slate-100 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 hover:bg-blue-50' : 'hover:bg-slate-50'}`}
                    >
                      {/* Session ID */}
                      <td className="px-4 py-3 text-xs text-slate-400 font-mono whitespace-nowrap">
                        {s.sessionId?.slice(0, 8)}…
                      </td>

                      {/* Visitor location + device */}
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs text-slate-700 font-medium">
                            {loc?.country ? `${countryFlag(loc.country)} ${loc.city || loc.country}` : '🌍 Unknown'}
                          </span>
                          <span className="text-[11px] text-slate-400 flex items-center gap-1">
                            {ua.device === 'Mobile' ? <Smartphone size={10} /> : <Monitor size={10} />}
                            {ua.browser} · {ua.os}
                          </span>
                        </div>
                      </td>

                      {/* Source */}
                      <td className="px-4 py-3">
                        <Badge value={s.source || 'other'} map={SOURCE_LABELS} colorMap={SOURCE_COLORS} />
                      </td>

                      {/* Language */}
                      <td className="px-4 py-3">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${s.language === 'ar' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}>
                          {s.language === 'ar' ? '🇦🇪 AR' : '🇬🇧 EN'}
                        </span>
                      </td>

                      {/* Message count */}
                      <td className="px-4 py-3 text-xs text-slate-600 text-center font-semibold">
                        {s.messageCount}
                      </td>

                      {/* Last message */}
                      <td className="px-4 py-3 text-xs text-slate-500 max-w-[180px] truncate">
                        {s.lastMessage || '—'}
                      </td>

                      {/* Lead */}
                      <td className="px-4 py-3 text-xs">
                        {s.leadCaptured
                          ? <span className="flex items-center gap-1 text-emerald-600 font-semibold"><Mail size={11} />{s.leadEmail || 'yes'}</span>
                          : <span className="text-slate-300">—</span>
                        }
                      </td>

                      {/* Date */}
                      <td className="px-4 py-3 whitespace-nowrap">
                        <p className="text-xs text-slate-600">{fmtDate(s.createdAt)}</p>
                        <p className="text-[11px] text-slate-400">{fmtTime(s.createdAt)}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="border-t border-slate-100 px-4 py-3 flex items-center justify-between">
              <span className="text-xs text-slate-400">Page {page} of {totalPages} · {total.toLocaleString()} total</span>
              <div className="flex items-center gap-1">
                <button onClick={() => fetchSessions(page - 1)} disabled={page === 1}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 transition-colors">
                  <ChevronLeft size={14} />
                </button>
                {pages.map(p => (
                  <button key={p} onClick={() => fetchSessions(p)}
                    className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${p === page ? 'bg-primary text-white' : 'border border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                    {p}
                  </button>
                ))}
                <button onClick={() => fetchSessions(page + 1)} disabled={page === totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 transition-colors">
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Slide-in Detail Panel */}
      <AnimatePresence>
        {selected && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelected(null)}
              className="fixed inset-0 bg-black/20 z-40"
            />
            <DetailPanel session={selected} onClose={() => setSelected(null)} />
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
